import React from 'react';

import Form, { useForm } from 'src/presentation/components/forms/Form';
import ExternalLink from 'src/presentation/components/pages/shared/ExternalLink';
import VoiceConfiguration from 'src/models/Channels/VoiceConfiguration';
import generateId from 'src/lib/generateId';
import useActivateNumberForVoice from 'src/presentation/components/pages/channels/shared/hooks/useActivateNumberForVoice';
import voiceConfigurationResource from 'src/actions/channels/voiceConfigurationResource';
import { AvailablePhoneNumber } from 'src/actions/availableNumbers/availableNumbersResource';
import CompanyPhoneNumber from 'src/models/CompanyPhoneNumber/CompanyPhoneNumber';
import { Button } from 'src/presentation/components/agent_desktop_components';
import { H3 } from '@ad-scripts/presentation/components/common/headers';
import { StageConfigState } from 'src/presentation/components/pages/channels/shared/helpers/stageConfiguration';
import {
  StepWizardActionContainer,
  StepWizardFieldWithBorder,
} from 'src/presentation/components/pages/email/components/StepWizardLayout';
import {
  StyledBold,
  StyledDescription,
  StyledRadioGroup,
} from 'src/presentation/components/pages/channels/shared/setupPhoneModal/templates/shared/elements';
import {
  useFormChange,
  useResetFormState,
} from 'src/presentation/components/pages/channels/shared/hooks/useFormChange';
import { formatPhone } from 'src/actions/shared/phonesHelper';

const initialWorkingState = { linkIvr: true };

const LinkPhoneNumberToIVR = ({
  nextStep,
  saveStep,
  isActive,
  stageConfig,
  phoneNumber,
}: {
  nextStep?;
  saveStep?;
  isActive?: boolean;
  phoneNumber: AvailablePhoneNumber | CompanyPhoneNumber;
  stageConfig?: StageConfigState;
}): JSX.Element | null => {
  const { activateNumber, isPersisting } = useActivateNumberForVoice({
    add: voiceConfigurationResource.effects.add,
    isActive,
    nextStep,
    saveStep,
    validate: voiceConfigurationResource.effects.validate,
    resetErrors: voiceConfigurationResource.actions.resetErrors,
  });

  const formContext = useForm({
    item: initialWorkingState,
    resetErrors: [],
    onSave: ({ workingState }) => {
      if (workingState.linkIvr) {
        nextStep();
        return;
      }

      const voiceConfiguration: VoiceConfiguration = {
        id: generateId(),
        number: phoneNumber.phoneNumber,
        queueIvrId: undefined,
        initialIvrId: undefined,
        queueIvrWaitDuration: 1,
      };

      activateNumber(voiceConfiguration);
    },
  });

  const { onSave, updateState } = formContext;
  useFormChange(formContext.workingState, saveStep);
  useResetFormState(initialWorkingState, updateState, stageConfig);

  return (
    <>
      <StepWizardFieldWithBorder>
        <H3 data-aid="wizard-title">Link your phone number to an IVR</H3>
        <StyledDescription>
          IVR (interactive voice response) is a telephony software that interacts with customers through voice or DTMF
          (dual-tone multi-frequency) system. Do you want to link{' '}
          <StyledBold>{formatPhone(phoneNumber.phoneNumber)}</StyledBold> to an IVR?{` `}
          <ExternalLink
            data-aid="learnAbout"
            label="Learn about IVR benefits"
            to="https://help.gladly.com/docs/benefits-of-using-an-ivr"
          />
        </StyledDescription>
        <Form context={formContext}>
          <StyledRadioGroup
            className="radioGroup"
            attr="linkIvr"
            data-aid="link-Ivr-radio-group"
            options={[
              {
                label: 'Yes, I want to link this number to an IVR',
                value: true,
                description: (
                  <>
                    When customers call this number, they’re routed to the first node (step) of an{` `}
                    <ExternalLink
                      data-aid="learnAbout"
                      label="initial IVR or queue IVR"
                      to="https://help.gladly.com/docs/initial-ivr-and-queue-ivr"
                    />
                    .
                  </>
                ),
              },
              {
                label:
                  'No, I don’t want to link this number to an IVR, I want to use this number as a direct line only',
                value: false,
                description: 'When customers call this number, they’re immediately routed to an Inbox.',
              },
            ]}
          />
        </Form>
      </StepWizardFieldWithBorder>
      <StepWizardActionContainer>
        <Button data-aid="editor-save" type={Button.Types.PRIMARY} onClick={onSave} isLoading={isPersisting}>
          Next
        </Button>
      </StepWizardActionContainer>
    </>
  );
};

export default LinkPhoneNumberToIVR;
